import type { CSSProperties, ElementType } from 'react'

import { Fragment, useMemo, useRef } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useMounted } from '@/hooks/use-mounted'

interface SplitHeadingProps {
    text: string
    as?: ElementType
    className?: string
    serifClassName?: string
    trigger?: 'view' | 'mount'
    delay?: number
    stagger?: number
    serifDelay?: number
    id?: string
}

type Token = { kind: 'word', value: string, serif: boolean } | { kind: 'break' }

const tokenize = (text: string): Array<Token> => {
    const tokens: Array<Token> = []

    text.split('*').forEach((segment, segmentIndex) => {
        const serif = segmentIndex % 2 === 1

        segment.split('\n').forEach((line, lineIndex) => {
            if (lineIndex > 0) tokens.push({ kind: 'break' })

            line.split(/\s+/).filter(Boolean).forEach(value => tokens.push({ kind: 'word', value, serif }))
        })
    })

    return tokens
}

export const plainText = (text: string): string => text.replace(/\*/g, '').replace(/\s*\n\s*/g, ' ').trim()

/**
 * Headline that rises word by word. Wrap words in *asterisks* to set them in
 * the italic serif; those words arrive a beat after the sans words.
 * A line break (\n) forces a new line from the small breakpoint up.
 */
export const SplitHeading = ({
    text,
    as: Tag = 'h2',
    className,
    serifClassName,
    trigger = 'view',
    delay = 0,
    stagger = 70,
    serifDelay = 280,
    id
}: SplitHeadingProps) => {
    const ref = useRef<HTMLElement>(null)
    const inView = useInView(ref, { rootMargin: '0px 0px -6% 0px' })
    const mounted = useMounted()
    const shown = trigger === 'mount' ? mounted : inView
    const tokens = useMemo(() => tokenize(text), [text])

    let wordIndex = -1

    return (
        <Tag
            ref={ref}
            id={id}
            data-split=''
            data-shown={shown ? '' : undefined}
            className={className}
        >
            <span className='sr-only'>
                {plainText(text)}
            </span>

            <span aria-hidden='true'>
                {tokens.map((token, index) => {
                    if (token.kind === 'break') return <br key={index} className='max-sm:hidden' />

                    wordIndex += 1

                    const wordDelay = delay + wordIndex * stagger + (token.serif ? serifDelay : 0)
                    const hasNext = index < tokens.length - 1

                    return (
                        <Fragment key={index}>
                            <span
                                className={cn('split-word', token.serif && cn('is-serif em-serif', serifClassName))}
                                style={{ '--word-delay': `${wordDelay}ms` } as CSSProperties}
                            >
                                <span>
                                    {token.value}
                                </span>
                            </span>

                            {hasNext ? ' ' : null}
                        </Fragment>
                    )
                })}
            </span>
        </Tag>
    )
}
